import {
  Link as TanstackRouterLink,
  type LinkProps as TanstackRouterLinkProps,
} from '@tanstack/react-router';
import type { CommonProps } from 'gooey';
import type React from 'react';
import { twMerge } from 'tailwind-merge';
import type * as TF from 'type-fest';

export type RouterLinkTo = Required<TanstackRouterLinkProps>['to'];

export type AnchorLinkProps = TF.SetRequired<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'>;

export type RouterLinkProps = TF.Merge<
  TanstackRouterLinkProps,
  React.PropsWithChildren<CommonProps<HTMLAnchorElement>>
>;

export type LinkProps<P extends RouterLinkProps | AnchorLinkProps> = P & {
  openInNewTab?: boolean;
};

/**
 * A universal link component that can be used to link to internal or external URLs.
 */
export function Link<P extends RouterLinkProps | AnchorLinkProps>(props: LinkProps<P>) {
  if (isRouterLink(props)) {
    return (
      <TanstackRouterLink
        {...buildProps(props)}
        className={twMerge('normal-case link link-hover text-gray-300', props.className)}
      >
        {props.children}
      </TanstackRouterLink>
    );
  }

  return (
    <a
      {...buildProps(props)}
      className={twMerge('normal-case link link-hover text-gray-300', props.className)}
    >
      {props.children}
    </a>
  );
}

/*
|------------------
| Internals
|------------------
*/

function isRouterLink(props: RouterLinkProps | AnchorLinkProps): props is RouterLinkProps {
  return 'to' in props;
}

function buildProps(props: LinkProps<RouterLinkProps | AnchorLinkProps>) {
  const { openInNewTab = false, ...restProps } = props;

  if (openInNewTab) {
    return {
      target: '_blank',
      rel: 'noopener noreferrer',
      ...restProps,
    };
  }
  return restProps;
}
